import { useEffect, useState } from "react";

import { getConfigs } from "app/utils/configs";

/** The docs page covering how to point a SAML provider at Stacklet. */
const SSO_DOCS_PATH = "/how-to/configure-sso/";

export interface SamlSetupConfig {
  /**
   * Cognito's assertion consumer service URL — where the IdP posts its SAML
   * response. Undefined while the configs load, and on a deployment whose
   * `cognito.json` predates the key.
   */
  acsUrl: string | undefined;
  /**
   * The audience the IdP must restrict its assertion to: the user pool's
   * service-provider entity ID. Undefined under the same conditions as
   * `acsUrl`.
   */
  audienceUri: string | undefined;
  /**
   * This deployment's SSO documentation. Undefined while the configs load, and
   * where no docs service is deployed.
   */
  docsUrl: string | undefined;
}

const NO_CONFIG: SamlSetupConfig = {
  acsUrl: undefined,
  audienceUri: undefined,
  docsUrl: undefined,
};

/**
 * The deployment values an operator needs to configure their SAML provider.
 *
 * `cognito.json` carries the two Cognito values and `application.json` the docs
 * service, and `getConfigs` reads both — so one hook over that one read hands
 * the setup instructions everything they show. Every field is optional: a
 * missing one costs its line of the instructions and nothing else, which is why
 * a failed read is swallowed here rather than surfaced.
 */
export function useSamlSetupConfig(): SamlSetupConfig {
  const [config, setConfig] = useState<SamlSetupConfig>(NO_CONFIG);

  useEffect(() => {
    let cancelled = false;

    getConfigs()
      .then(({ cognito, docs }) => {
        if (cancelled) {
          return;
        }
        setConfig({
          acsUrl: cognito.cognito_acs_url,
          audienceUri: cognito.cognito_sp_entity_id,
          // The block's URL carries no trailing slash, but a deployment that
          // publishes one must not turn the path into `//how-to/...`.
          docsUrl: docs && `${docs.url.replace(/\/+$/, "")}${SSO_DOCS_PATH}`,
        });
      })
      // Instructions are an aid, not the form: a failed read leaves them out
      // rather than taking the page with it. `getConfigs` reports its own
      // failures, so there is nothing to add here.
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, []);

  return config;
}
